/** One-shot display of recovery codes — shown after TOTP activation
 * and after a manual Regenerate. The codes are visible exactly once;
 * once the user dismisses this panel they're gone from memory.
 *
 * UX:
 *   - 10 codes in a 2-column mono grid so they're readable on phone
 *     screens without word-wrap.
 *   - Copy-all + Download .txt — users almost always pick one of
 *     these over hand-copying a single code.
 *   - "I've saved these" confirmation gate so a user can't dismiss
 *     by mistake. Phrasing borrowed from GitHub's recovery-codes UX.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";


type Props = {
  codes: string[];
  onAcknowledged: () => void;
  /** Optional headline override — e.g. "Save these recovery codes" for
   * the initial-activation flow vs "New recovery codes" for regenerate. */
  title?: string;
};


export function BackupCodesPanel({ codes, onAcknowledged, title }: Props) {
  const { t } = useTranslation();
  const [confirmed, setConfirmed] = useState(false);
  const [copied, setCopied] = useState(false);

  async function copyAll() {
    try {
      await navigator.clipboard.writeText(codes.join("\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard unavailable; user can manually select */
    }
  }

  function download() {
    const blob = new Blob(
      [
        "NexBasira — MFA recovery codes\n",
        "Each code can be used ONCE to disable MFA on your account\n",
        "if you lose access to your authenticator app.\n",
        "Keep this file somewhere safe (password manager, encrypted disk).\n\n",
        codes.join("\n") + "\n",
      ],
      { type: "text/plain" },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "nexbasira-recovery-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-4">
      <header className="space-y-1">
        <h2 className="text-lg font-semibold">
          {title ?? t("ui.backupCodesPanel.title")}
        </h2>
        <p className="text-sm text-nb-text-2">
          {t("ui.backupCodesPanel.intro")}
        </p>
      </header>

      <ul className="grid grid-cols-2 gap-2 rounded-md border border-nb-border bg-nb-bg p-3 font-mono text-sm">
        {codes.map((c) => (
          <li key={c} className="text-nb-text">
            {c}
          </li>
        ))}
      </ul>

      <div className="flex flex-wrap gap-2">
        <button type="button" className="btn-ghost text-sm" onClick={copyAll}>
          {copied ? t("ui.backupCodesPanel.copied") : t("ui.backupCodesPanel.copyAll")}
        </button>
        <button type="button" className="btn-ghost text-sm" onClick={download}>
          {t("ui.backupCodesPanel.download")}
        </button>
      </div>

      <label className="flex items-start gap-2 text-sm text-nb-text-2">
        <input
          type="checkbox"
          className="mt-1"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
        />
        <span>{t("ui.backupCodesPanel.confirmLabel")}</span>
      </label>

      <div className="flex justify-end">
        <button
          type="button"
          className="btn-primary text-sm"
          onClick={onAcknowledged}
          disabled={!confirmed}
        >
          {t("ui.backupCodesPanel.done")}
        </button>
      </div>
    </div>
  );
}
